import { Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import * as Sentry from '@sentry/react';
import React, { type ErrorInfo, type PropsWithChildren } from 'react';

import { Text, Title, isTruthy } from 'common';

import { ReactComponent as ErrorIconSvg } from './error.svg';

interface Props extends PropsWithChildren {}

/**
 * `digest` is supplied by react-dom rather than by React itself, so it is not
 * part of the `ErrorInfo` contract and is absent on most error paths.
 */
interface ErrorInfoWithDigest extends ErrorInfo {
  digest?: string;
}

export class ErrorBoundary extends React.Component<Props> {
  state = { error: undefined };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: unknown, info: ErrorInfoWithDigest) {
    if (isTruthy(info.digest)) {
      console.error(info.digest);
      Sentry.captureMessage(info.digest);
    }
    console.error(error);
    Sentry.captureException(error);
  }

  render() {
    if (this.state.error) {
      return (
        <Stack alignItems="center" gap={3} paddingY={20}>
          <ErrorIcon />
          <Title component="h1" variant="heading1" bold>
            Oops! Something went wrong.
          </Title>
          <Text variant="body2" bold>
            The page encountered an error, and we're on it. Please refresh or try again later.
            Thanks for your patience!
          </Text>
        </Stack>
      );
    }
    return this.props.children;
  }
}

const ErrorIcon = styled(ErrorIconSvg)`
  width: 154px;
  height: 154px;
`;
